import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../services/auth.service';
import {
  ModelCatalogProvider,
  ModelCatalogTier,
  Tutor,
  TutorService,
  TutorSettingItem,
} from '../../../services/tutor.service';

/** Brand monogram (initial + colour) shown as a placeholder "logo" in front of
 *  each row, keyed by the base brand (the -Max / -Cheap suffix is stripped).
 *  Real provider SVG logos can replace these later without touching the list. */
const BRAND_BADGE: Record<string, { initial: string; color: string }> = {
  OpenAI: { initial: 'O', color: '#10a37f' },
  Anthropic: { initial: 'A', color: '#d97757' },
  Google: { initial: 'G', color: '#4285f4' },
  Meta: { initial: 'M', color: '#0668e1' },
  Groq: { initial: 'q', color: '#f55036' },
  Qwen: { initial: 'Q', color: '#615ced' },
  DeepSeek: { initial: 'D', color: '#4d6bfe' },
};

/** Brands with a real SVG logo shipped in
 *  `assets/images/model-providers/<brand>.svg` (Simple Icons, CC0). Any brand
 *  not listed falls back to the coloured monogram. Groq has no logo yet. */
const LOGO_BRANDS = new Set([
  'OpenAI',
  'Anthropic',
  'Google',
  'Meta',
  'Qwen',
  'DeepSeek',
]);

/** Agent features that make a turn demanding (many tools, multi-step
 *  flows). A model with `weak_on: complex` gets a warning when one is on. */
const DEMANDING_FEATURES = [
  'guided_mode_enabled',
  'exercise_mode',
  'debate_mode_enabled',
  'agent_mcp_enabled',
];

const AGENT_LOOP_FLAG = 'agent_loop_on_selected_model';

/** Strongest first — orders a vendor's models inside a tab. */
const CLASS_RANK: Record<string, number> = { frontier: 3, strong: 2, standard: 1, light: 0 };

/** One tile: a model, and — where the same model also exists on a faster
 *  service tier — that speed variant behind a switch on the same tile. */
export interface ModelTile {
  base: ModelCatalogProvider;
  fast?: ModelCatalogProvider;
}

function isOn(setting?: TutorSettingItem): boolean {
  const v = setting?.value ?? setting?.default_value;
  return v === true || v === 'true' || v === 'on' || v === 1 || v === '1';
}

/**
 * Granular AI-provider picker (additive alternative to the plain `ai_provider`
 * dropdown). Three segment tabs — Premium (top) / Marktführer (flagship) /
 * Kosteneffizient (value) — each listing providers with a friendly description
 * and the current answer model named dynamically (from the catalog). Every
 * option shows its own credit factor (the provider's, the one billed — since
 * al-user-management migration 079 the segment is only a grouping); the
 * credit legend below shows per segment the span of those factors, built by
 * al-tutor from the same options (bug #214), plus a "models last updated" date.
 *
 * USER-FACING RULE: only the answer (large) model is ever surfaced — never the
 * small/large pair. The small router model stays internal.
 *
 * IMPORTANT: this does NOT change routing — selecting an option writes the exact
 * same `ai_provider` template setting the dropdown wrote. Gated behind a single
 * flag in `ai-settings.component.ts`; flip it off to fall back to the dropdown.
 */
@Component({
  selector: 'app-model-selector',
  templateUrl: './model-selector.component.html',
  styleUrl: './model-selector.component.scss',
})
export class ModelSelectorComponent implements OnInit, OnChanges {
  @Input() tutor?: Tutor;
  @Input() providerSetting?: TutorSettingItem;
  /** The agent's settings (and those of a linked Twin, whose functions the
   *  agent uses) — read for the agent-loop flag and demanding features. */
  @Input() settings: TutorSettingItem[] = [];
  @Input() linkedSettings: TutorSettingItem[] = [];
  @Output() settingValueSaved = new EventEmitter<{
    tutorId: string;
    settingId: string;
    value: any;
  }>();

  providers: ModelCatalogProvider[] = [];
  /** Tiles per tab, built once per catalog load (stable arrays). */
  private tiles: Record<string, ModelTile[]> = {};
  segments: string[] = ['top', 'flagship', 'value'];
  tiers: ModelCatalogTier[] = [];
  lastUpdated?: string;
  agentLoopMode: 'legacy' | 'opt_in' | 'on' = 'legacy';
  /** Enabled demanding features (i18n keys), recomputed on input change. */
  demandingFeatures: string[] = [];
  loopFlagSetting?: TutorSettingItem;
  savingLoopFlag = false;
  readonly isSuperAdmin: boolean;

  loading = true;
  error = false;
  saving = false;
  selected?: string;
  activeSegment = 'flagship';

  constructor(
    private tutorService: TutorService,
    private toastr: NbToastrService,
    private translate: TranslateService,
    authService: AuthService
  ) {
    this.isSuperAdmin = authService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.selected = this.currentValue();
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['providerSetting']) {
      this.selected = this.currentValue();
    }
    if (changes['settings'] || changes['linkedSettings']) {
      this.readAgentSettings();
    }
  }

  private readAgentSettings(): void {
    const all = [...(this.settings || []), ...(this.linkedSettings || [])];
    this.loopFlagSetting = (this.settings || []).find(
      (s) => s.variable_name === AGENT_LOOP_FLAG
    );
    this.demandingFeatures = DEMANDING_FEATURES.filter((v) =>
      all.some((s) => s.variable_name === v && isOn(s))
    );
  }

  private currentValue(): string | undefined {
    const v = this.providerSetting?.value ?? this.providerSetting?.default_value;
    return v != null && v !== '' ? String(v) : undefined;
  }

  private load(): void {
    this.loading = true;
    this.error = false;
    this.tutorService.fetchModelCatalog().subscribe({
      next: (catalog) => {
        const allowed = this.providerSetting?.options || [];
        this.providers = allowed.length
          ? catalog.providers.filter((p) => allowed.includes(p.provider))
          : catalog.providers;
        if (catalog.segments?.length) this.segments = catalog.segments;
        this.tiers = catalog.tiers || [];
        this.lastUpdated = catalog.last_updated;
        this.agentLoopMode = catalog.agent_loop || 'legacy';
        this.tiles = this.buildTiles(this.providers);
        const cur = this.providers.find((p) => p.provider === this.selected);
        this.activeSegment = cur?.segment || this.firstNonEmptySegment();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = true;
      },
    });
  }

  private firstNonEmptySegment(): string {
    const preferred = ['flagship', 'value', 'top'];
    return preferred.find((s) => this.segmentCount(s) > 0) || this.segments[0];
  }

  /** Speed variants fold into the tile of their base model (same model, same
   *  vendor, same tab); tiles are grouped by vendor in the catalog's vendor
   *  order, a vendor's models from the strongest down. */
  private buildTiles(providers: ModelCatalogProvider[]): Record<string, ModelTile[]> {
    const vendorOrder: string[] = [];
    for (const p of providers) {
      if (!vendorOrder.includes(this.brand(p))) vendorOrder.push(this.brand(p));
    }
    const isVariantOf = (v: ModelCatalogProvider, b: ModelCatalogProvider) =>
      !!v.speed && !b.speed && v.model === b.model && v.segment === b.segment &&
      this.brand(v) === this.brand(b);
    const out: Record<string, ModelTile[]> = {};
    for (const seg of new Set(providers.map((p) => p.segment))) {
      const inSeg = providers.filter((p) => p.segment === seg);
      const variants = inSeg.filter((v) => inSeg.some((b) => isVariantOf(v, b)));
      const tiles = inSeg
        .filter((p) => !variants.includes(p))
        .map((base) => ({ base, fast: variants.find((v) => isVariantOf(v, base)) }));
      tiles.sort(
        (a, b) =>
          vendorOrder.indexOf(this.brand(a.base)) - vendorOrder.indexOf(this.brand(b.base)) ||
          (CLASS_RANK[b.base.model_class || ''] ?? -1) - (CLASS_RANK[a.base.model_class || ''] ?? -1) ||
          b.base.credit_multiplier - a.base.credit_multiplier
      );
      out[seg] = tiles;
    }
    return out;
  }

  private readonly noTiles: ModelTile[] = [];

  tilesIn(segment: string): ModelTile[] {
    return this.tiles[segment] || this.noTiles;
  }

  segmentCount(segment: string): number {
    return this.tilesIn(segment).length;
  }

  /** The provider a tile stands for right now: its speed variant while that
   *  is the saved choice, else the base model. */
  active(t: ModelTile): ModelCatalogProvider {
    return t.fast && this.selected === t.fast.provider ? t.fast : t.base;
  }

  isTileSelected(t: ModelTile): boolean {
    return this.selected === t.base.provider || (!!t.fast && this.selected === t.fast.provider);
  }

  fastOn(t: ModelTile): boolean {
    return !!t.fast && this.selected === t.fast.provider;
  }

  /** Clicking a tile picks its base model; a tile already chosen stays as it
   *  is (the speed switch changes the variant). */
  selectTile(t: ModelTile): void {
    if (this.isTileSelected(t)) return;
    this.select(t.base);
  }

  /** Speed switch: flips between the base model and its faster variant —
   *  and picks the tile if it was not the chosen one. */
  toggleFast(t: ModelTile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!t.fast) return;
    this.select(this.fastOn(t) ? t.base : t.fast);
  }

  trackTile = (_: number, t: ModelTile) => t.base.provider;

  isSelected(p: ModelCatalogProvider): boolean {
    return this.selected === p.provider;
  }

  /** i18n tab label for a segment (bilingual, single source). */
  /** "3–5" or "0.2": the span of factors offered in a segment. */
  tierFactor(t: ModelCatalogTier): string {
    const min = t.multiplier_min ?? t.multiplier;
    const max = t.multiplier_max ?? min;
    return min === max ? `${min}` : `${min}–${max}`;
  }

  segmentLabel(segment: string): string {
    return this.translate.instant(
      `pages.tutors.modelSelector.tabs.${segment}`
    );
  }

  /** Friendly provider label from i18n, falling back to the raw provider id. */
  providerName(p: ModelCatalogProvider): string {
    const key = `pages.tutors.modelSelector.providers.${p.provider}.name`;
    const t = this.translate.instant(key);
    return t === key ? p.provider : t;
  }

  /** Short helper description; may interpolate {{model}} (the current answer
   *  model, injected dynamically so copy updates when the catalog changes). */
  providerDesc(p: ModelCatalogProvider): string {
    const key = `pages.tutors.modelSelector.providers.${p.provider}.desc`;
    const t = this.translate.instant(key, { model: p.model_label || p.model });
    return t === key ? '' : t;
  }

  /** Category + model + credit-cost mini-info, specific to this option. */
  creditTooltip(p: ModelCatalogProvider): string {
    return this.translate.instant('pages.tutors.modelSelector.creditTooltip', {
      category: this.segmentLabel(p.segment),
      model: p.model_label || p.model,
      mult: p.credit_multiplier,
    });
  }

  /** True when this agent's loop runs on the selected model — only then do
   *  agent suitability and its hints matter for the customer. */
  get agentLoopActive(): boolean {
    return (
      this.agentLoopMode === 'on' ||
      (this.agentLoopMode === 'opt_in' && isOn(this.loopFlagSetting))
    );
  }

  /** Suitability chips: for everyone once the loop runs on the chosen model,
   *  always for super_admins (they switch it on). */
  get showAgentInfo(): boolean {
    return this.agentLoopActive || this.isSuperAdmin;
  }

  agentStatus(p: ModelCatalogProvider): string | null {
    return p.agent?.status || null;
  }

  /** Key for chip text, tooltip and chip colour. A market-leader or premium
   *  model with notes reads "suited, mind the notes" instead of "with
   *  limitations" — the concrete note still shows below the list. */
  agentStatusKey(p: ModelCatalogProvider): string {
    const status = p.agent!.status;
    return status === 'hint' && p.segment !== 'value' ? 'hintLeader' : status;
  }

  private isGoogle(p: ModelCatalogProvider): boolean {
    return p.provider.startsWith('Google');
  }

  agentLabel(p: ModelCatalogProvider): string {
    return this.translate.instant(
      `pages.tutors.modelSelector.agent.status.${this.agentStatusKey(p)}`
    );
  }

  agentTooltip(p: ModelCatalogProvider): string {
    const status = p.agent!.status;
    const parts = [
      this.translate.instant(
        `pages.tutors.modelSelector.agent.statusHint.${this.agentStatusKey(p)}`
      ),
    ];
    for (const w of p.agent!.weak_on || []) {
      parts.push(
        this.translate.instant(`pages.tutors.modelSelector.agent.weakOn.${w}`)
      );
    }
    if (status !== 'answer_only' && p.agent!.stream === false) {
      parts.push(
        this.translate.instant('pages.tutors.modelSelector.agent.noStream')
      );
    }
    return parts.join(' ');
  }

  get selectedProvider(): ModelCatalogProvider | undefined {
    return this.providers.find((p) => p.provider === this.selected);
  }

  /** Hint lines for the selected model — only where they take effect
   *  (the loop runs on it) or for super_admins. */
  get selectedHints(): { key: string; params?: Record<string, string> }[] {
    const p = this.selectedProvider;
    if (!p?.agent || !this.showAgentInfo) return this.noHints;
    const sig = [
      p.provider,
      this.demandingFeatures.join(','),
      this.agentLoopActive,
      this.providers.length,
    ].join('|');
    if (sig === this.hintsSig) return this.hintsCache;
    const hints: { key: string; params?: Record<string, string> }[] = [];
    const weak = p.agent.weak_on || [];
    if (p.agent.status === 'hint' && weak.includes('complex') && this.demandingFeatures.length) {
      const rec = this.recommendedFor(p);
      hints.push({
        key: rec
          ? 'pages.tutors.modelSelector.agent.complexWarning'
          : 'pages.tutors.modelSelector.agent.complexWarningNoRec',
        params: {
          features: this.demandingFeatures
            .map((f) => this.translate.instant(`pages.tutors.modelSelector.agent.features.${f}`))
            .join(', '),
          model: rec ? rec.model_label || rec.model : '',
        },
      });
    }
    for (const w of weak) {
      if (w === 'complex' && this.demandingFeatures.length) continue;
      hints.push({ key: `pages.tutors.modelSelector.agent.weakOn.${w}` });
    }
    if (this.isGoogle(p)) {
      hints.push({ key: 'pages.tutors.modelSelector.agent.googleAvailability' });
    }
    if (p.agent.status === 'answer_only') {
      hints.push({ key: 'pages.tutors.modelSelector.agent.answerOnlyHint' });
    } else if (p.agent.stream === false) {
      hints.push({ key: 'pages.tutors.modelSelector.agent.noStream' });
    }
    this.hintsSig = sig;
    this.hintsCache = hints;
    return hints;
  }

  private readonly noHints: { key: string; params?: Record<string, string> }[] = [];
  private hintsSig = '';
  private hintsCache: { key: string; params?: Record<string, string> }[] = [];

  /** A fully suitable model in the same segment (same credit factor), else
   *  the cheapest fully suitable one overall. */
  private recommendedFor(p: ModelCatalogProvider): ModelCatalogProvider | undefined {
    const certified = this.providers.filter(
      (c) => c.agent?.status === 'certified' && c.provider !== p.provider
    );
    return (
      certified.find((c) => c.segment === p.segment) ||
      [...certified].sort((a, b) => a.credit_multiplier - b.credit_multiplier)[0]
    );
  }

  /** super_admin: run this agent's loop on the selected model while the
   *  global switch is `opt_in`. Same setting write as every other toggle. */
  toggleAgentLoop(enabled: boolean): void {
    const setting = this.loopFlagSetting;
    if (!this.tutor || !setting || this.savingLoopFlag) return;
    const previous = setting.value;
    setting.value = enabled;
    this.savingLoopFlag = true;
    this.tutorService
      .updateTutorSettings(this.tutor.id, { [setting.setting_id + '']: enabled })
      .subscribe({
        next: () => {
          this.savingLoopFlag = false;
          this.settingValueSaved.emit({
            tutorId: this.tutor!.id,
            settingId: String(setting.setting_id),
            value: enabled,
          });
        },
        error: () => {
          this.savingLoopFlag = false;
          setting.value = previous;
          this.toastr.danger(
            this.translate.instant('pages.tutors.modelSelector.saveError'),
            this.translate.instant('pages.tutors.modelSelector.errorTitle')
          );
        },
      });
  }

  badgeClass(p: ModelCatalogProvider): string {
    return `ms-badge--${p.segment}`;
  }

  /** Base brand: 'OpenAI-Cheap-Fast' / 'OpenAI-Astra' → 'OpenAI'. */
  private brand(p: ModelCatalogProvider): string {
    return p.provider.split('-')[0];
  }

  speedLabel(p: ModelCatalogProvider): string {
    return this.translate.instant(`pages.tutors.modelSelector.speed.${p.speed}`);
  }

  /** Speed + cost hint for an option (tooltip) and the selected one (line). */
  speedHint(p: ModelCatalogProvider): string {
    const key = p.provider.startsWith('Groq')
      ? 'pages.tutors.modelSelector.speed.hintGroq'
      : `pages.tutors.modelSelector.speed.hint_${p.speed}`;
    return this.translate.instant(key, { mult: p.credit_multiplier });
  }

  brandInitial(p: ModelCatalogProvider): string {
    return BRAND_BADGE[this.brand(p)]?.initial || p.provider.charAt(0);
  }

  brandColor(p: ModelCatalogProvider): string {
    return BRAND_BADGE[this.brand(p)]?.color || '#6b7280';
  }

  hasLogo(p: ModelCatalogProvider): boolean {
    return LOGO_BRANDS.has(this.brand(p));
  }

  logoSrc(p: ModelCatalogProvider): string {
    return `assets/images/model-providers/${this.brand(p).toLowerCase()}.svg`;
  }

  select(p: ModelCatalogProvider): void {
    if (this.saving || !this.tutor || !this.providerSetting) return;
    if (this.selected === p.provider) return;

    const previous = this.selected;
    this.selected = p.provider; // optimistic
    this.saving = true;

    this.tutorService
      .updateTutorSettings(this.tutor.id, {
        [this.providerSetting.setting_id + '']: p.provider,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.settingValueSaved.emit({
            tutorId: this.tutor!.id,
            settingId: String(this.providerSetting!.setting_id),
            value: p.provider,
          });
        },
        error: () => {
          this.saving = false;
          this.selected = previous;
          this.toastr.danger(
            this.translate.instant('pages.tutors.modelSelector.saveError'),
            this.translate.instant('pages.tutors.modelSelector.errorTitle')
          );
        },
      });
  }
}
